import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

export default function Reader() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [itin, setItin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(1);
  const dayRefs = useRef({});

  useEffect(() => {
    api.get(`/library/${slug}`)
      .then((r) => { setItin(r.data); setLoading(false); })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("This route isn't in your library yet.");
          navigate("/library", { replace: true });
        } else {
          toast.error("Could not load itinerary");
          setLoading(false);
        }
      });
  }, [slug, navigate]);

  // Track scroll position to update active day
  useEffect(() => {
    if (!itin) return;
    const onScroll = () => {
      const offset = 200;
      let current = 1;
      for (const day of itin.days) {
        const el = dayRefs.current[day.day_number];
        if (el && el.getBoundingClientRect().top < offset) current = day.day_number;
      }
      setActiveDay(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [itin]);

  const scrollToDay = (n) => {
    const el = dayRefs.current[n];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)] flex items-center justify-center">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink3)]">◆ Loading…</div>
      </div>
    );
  }
  if (!itin) return null;

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="reader-page">
      {/* Compact reader navbar */}
      <div className="bg-[var(--paper)]/95 backdrop-blur-md border-b border-[var(--line)] sticky top-0 z-40 print:hidden">
        <div className="max-w-[820px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Logo size={22} />
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              data-testid="pdf-export"
              className="btn btn-ghost btn-sm"
            >
              ↓ PDF
            </button>
            <Link to="/library" data-testid="back-to-library" className="btn btn-paper btn-sm">
              ← Library
            </Link>
          </div>
        </div>
        {/* Day tabs */}
        <div
          className="max-w-[820px] mx-auto px-6 pb-3 flex gap-2 overflow-x-auto scrollbar-thin"
          data-testid="day-tabs"
        >
          {(itin.days ?? []).map((d) => (
            <button
              key={d.day_id}
              onClick={() => scrollToDay(d.day_number)}
              data-testid={`day-tab-${d.day_number}`}
              className={`pill flex-shrink-0 ${activeDay === d.day_number ? "on" : ""}`}
            >
              Day {d.day_number}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <article className="max-w-[820px] mx-auto px-6 py-12">
        <span className="eyebrow">◆ {itin.destination_country} · {itin.duration_days} days</span>
        <h1 className="h-display text-[36px] sm:text-[48px] mt-3 leading-[1]">{itin.title}</h1>
        <p className="mt-4 text-[14px] text-[var(--ink2)]">
          by <strong>{itin.seller?.name}</strong>
        </p>

        <div className="mt-12 space-y-12">
          {(itin.days ?? []).map((d) => (
            <section
              key={d.day_id}
              ref={(el) => (dayRefs.current[d.day_number] = el)}
              data-testid={`reader-day-${d.day_number}`}
              className="scroll-mt-32"
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="serif text-[60px] text-[var(--red)] leading-none">
                  {String(d.day_number).padStart(2, "0")}
                </span>
                <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink3)]">Day {d.day_number}</div>
              </div>
              <h2 className="h-display text-[28px] sm:text-[34px] leading-[1.05] mt-2">{d.day_title}</h2>

              <div className="mt-8 space-y-8">
                {(d.blocks ?? []).map((b) => (
                  <BlockCard key={b.block_id} block={b} />
                ))}
              </div>
            </section>
          ))}

          {/* Extras */}
          {itin.extras && (
            <section className="border-t border-dashed border-[var(--line)] pt-12 mt-16" data-testid="extras">
              <span className="eyebrow">◆ Extras</span>
              <h2 className="h-display text-[28px] sm:text-[34px] mt-2">{itin.seller?.name?.split(" ")[0]}'s complete guide</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {[
                  ["🍜 Restaurants", itin.extras.restaurants],
                  ["🏨 Accommodation", itin.extras.accommodation],
                  ["🎒 Packing list", itin.extras.packing_list],
                  ["🚆 Getting around", itin.extras.getting_around],
                  ["🆘 Emergency", itin.extras.emergency_contacts],
                ].filter(([, v]) => v).map(([title, body]) => (
                  <div key={title} className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-5">
                    <h4 className="text-[14px] font-extrabold mb-2">{title}</h4>
                    <p className="text-[13px] leading-[1.6] text-[var(--ink2)]">{body}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white !important; }
          img { max-width: 100% !important; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function BlockCard({ block }) {
  const mapsUrl = block.location_name
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(block.location_name)}`
    : null;
  return (
    <div className="bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-[18px] p-5 sm:p-6">
      <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--red)] mb-2">
        {block.time_of_day}
      </div>
      <h4 className="text-[19px] font-extrabold tracking-[-0.01em] leading-tight mb-2">{block.activity_name}</h4>
      <p className="text-[14px] leading-[1.65] text-[var(--ink2)]">{block.description}</p>

      {block.photo_url && (
        <img
          src={block.photo_url}
          alt={block.activity_name}
          data-testid="block-photo"
          className="mt-4 w-full rounded-[14px] max-h-[360px] object-cover border border-[var(--line)] print:break-inside-avoid"
        />
      )}

      {block.creator_tip && (
        <div className="mt-4 border-l-[3px] border-[var(--red)] pl-3 italic text-[13px] text-[var(--ink)] leading-[1.55] bg-[#FCEAEC]/40 py-2 pr-3 rounded-r">
          <span className="not-italic mono text-[9px] tracking-[0.2em] uppercase text-[var(--red)] block mb-1">◆ Creator tip</span>
          {block.creator_tip}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="tag hover:border-[var(--red)] hover:text-[var(--red)] transition-colors"
          >
            📍 {block.location_name}
          </a>
        )}
        {block.approx_cost && <span className="tag" style={{ color: "var(--red)", borderColor: "var(--red)" }}>{block.approx_cost}</span>}
      </div>
    </div>
  );
}
